<script setup>
import { ref, reactive, onMounted, watch, computed, nextTick, onBeforeUnmount } from "vue";
import * as bootstrap from "bootstrap";
import { useBudgetStore } from "@/stores/budgetStore";
import { useTaxStore } from "@/stores/taxStore";
import { useProjectStore } from "@/stores/projectStore";
import { useAccountCategoryStore } from "@/stores/accountCategoryStore";
import * as Toast from "@/utils/toast";

const budgetStore = useBudgetStore();
const taxStore = useTaxStore?.() ?? null;
const projectStore = useProjectStore?.() ?? null;
const categoryStore = useAccountCategoryStore?.() ?? null;

/* Modals */
const mainModalEl = ref(null);
const deleteModalEl = ref(null);
let mainModalInstance = null;
let deleteModalInstance = null;

const isEdit = ref(false);
const saving = ref(false);
const deletingId = ref(null);
const taxesLoading = ref(false);

/* Focus refs */
const titleInputEl = ref(null);

/* ---------------- PROJECT / CATEGORY DROPDOWNS ---------------- */
const projectQuery = ref("");
const projectOptions = ref([]);
const projectsLoading = ref(false);
const selectedProject = ref(null);

const categoryQuery = ref("");
const categoryOptions = ref([]);
const categoriesLoading = ref(false);
const selectedCategory = ref(null);

/* Dates */
const startDate = ref(new Date());
const endDate = ref(new Date());
const dateFormat = "dd-MM-yyyy";

/* ---------------- TAX DROPDOWN ---------------- */
const taxQuery = ref("");
const taxOptions = ref([]);
const selectedTax = ref(null);

/* Revenue & Expense items */
const revenueItems = ref([{ title: "", amount: 0 }]);
const expenseItems = ref([{ title: "", amount: 0 }]);

/* UX validation */
const attempted = ref(false);
const errors = computed(() => {
  const e = {};
  if (!form.title || !String(form.title).trim()) e.title = "Budget title is required.";
  if (form.budget_type === "project" && !selectedProject.value) e.link = "Select a project for this budget.";
  if (form.budget_type === "category" && !selectedCategory.value) e.link = "Select a category for this budget.";
  // date sanity
  if (startDate.value && endDate.value && startDate.value > endDate.value) e.dates = "End date must be after start date.";
  return e;
});
const isValid = computed(() => Object.keys(errors.value).length === 0);

const notifyError = (msg) => {
  if (Toast?.toast?.error) return Toast.toast.error(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "error");
  if (Toast?.error) return Toast.error(msg);
  if (typeof Toast === "function") return Toast(msg, "error");
  console.error(msg);
};

/* Main form (aggregates) */
const form = reactive({
  id: null,
  title: "",
  budget_type: "category", // 'project' | 'category'
  total_revenue: 0, // A
  total_expense: 0, // B
  expected_profit: 0, // C = A - B
  tax_amount: 0, // D
  budget_amount: 0, // E = C - D
  notes: "",
  is_active: true,
});

/* Helpers: dates without timezone shift */
function toYmd(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fromYmd(value) {
  if (!value) return new Date();
  const [y, m, d] = String(value).split("-");
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? new Date() : date;
}

/* TAX: pick one from dropdown */
function chooseTax(option) {
  selectedTax.value = option || null;
  computeTotals();
}

/* Compute totals A, B, C, D, E */
function computeTotals() {
  let totalRev = 0;
  let totalExp = 0;

  revenueItems.value.forEach((r) => {
    totalRev += Number(r.amount || 0);
  });
  expenseItems.value.forEach((e) => {
    totalExp += Number(e.amount || 0);
  });

  form.total_revenue = totalRev;
  form.total_expense = totalExp;
  form.expected_profit = totalRev - totalExp;

  if (selectedTax.value) {
    const rate = Number(selectedTax.value.rate || 0);
    form.tax_amount = (form.expected_profit * rate) / 100;
  } else {
    form.tax_amount = Number(form.tax_amount || 0);
  }

  form.budget_amount = form.expected_profit - Number(form.tax_amount || 0);
}

/* Load tax options */
async function loadTaxOptions(q = "") {
  if (!taxStore) return;

  try {
    taxesLoading.value = true;

    let res;
    if (typeof taxStore.fetch === "function") {
      res = await taxStore.fetch({ q, per_page: 50 });
    } else if (typeof taxStore.list === "function") {
      res = await taxStore.list({ q, per_page: 50 });
    }

    const arr = (res && res.data && res.data.data) || taxStore.items || taxStore.taxes || [];
    taxOptions.value = arr.map((t) => {
      const rate = Number(t.rate || t.tax_rate || 0);
      return { id: t.id, name: t.name, rate, label: `${t.name} (${rate}%)` };
    });
  } catch (e) {
    console.error("Failed to load taxes", e);
  } finally {
    taxesLoading.value = false;
  }
}

/* Revenue actions */
function addRevenueRow() {
  revenueItems.value.push({ title: "", amount: 0 });
}
function removeRevenueRow(index) {
  if (revenueItems.value.length === 1) return;
  revenueItems.value.splice(index, 1);
  computeTotals();
}

/* Expense actions */
function addExpenseRow() {
  expenseItems.value.push({ title: "", amount: 0 });
}
function removeExpenseRow(index) {
  if (expenseItems.value.length === 1) return;
  expenseItems.value.splice(index, 1);
  computeTotals();
}

/* Reset */
function resetForm() {
  form.id = null;
  form.title = "";
  form.budget_type = "category";
  form.total_revenue = 0;
  form.total_expense = 0;
  form.expected_profit = 0;
  form.tax_amount = 0;
  form.budget_amount = 0;
  form.notes = "";
  form.is_active = true;

  startDate.value = new Date();
  endDate.value = new Date();

  revenueItems.value = [{ title: "", amount: 0 }];
  expenseItems.value = [{ title: "", amount: 0 }];

  selectedTax.value = null;
  taxQuery.value = "";

  selectedProject.value = null;
  projectQuery.value = "";
  selectedCategory.value = null;
  categoryQuery.value = "";

  attempted.value = false;
}

/* Submit */
async function submit() {
  attempted.value = true;
  computeTotals();

  if (!isValid.value) {
    notifyError(Object.values(errors.value)[0] || "Please fix the errors and try again.");
    nextTick(() => titleInputEl.value?.focus?.());
    return;
  }

  const payload = {
    title: form.title,
    budget_type: form.budget_type,
    start_date: toYmd(startDate.value),
    end_date: toYmd(endDate.value),
    tax_amount: form.tax_amount,
    notes: form.notes,
    is_active: form.is_active,

    total_revenue: form.total_revenue,
    total_expense: form.total_expense,
    expected_profit: form.expected_profit,
    budget_amount: form.budget_amount,

    revenue_items: revenueItems.value
      .filter((r) => r.title || r.amount)
      .map((r) => ({ title: r.title, amount: Number(r.amount || 0) })),
    expense_items: expenseItems.value
      .filter((e) => e.title || e.amount)
      .map((e) => ({ title: e.title, amount: Number(e.amount || 0) })),
  };

  if (form.budget_type === "project" && selectedProject.value) {
    payload.project_id = selectedProject.value.id;
    payload.account_category_id = null;
  } else if (form.budget_type === "category" && selectedCategory.value) {
    payload.account_category_id = selectedCategory.value.id;
    payload.project_id = null;
  } else {
    payload.project_id = null;
    payload.account_category_id = null;
  }

  if (selectedTax.value) {
    payload.tax_id = selectedTax.value.id;
    payload.tax_rate = selectedTax.value.rate;
  }

  saving.value = true;

  try {
    if (isEdit.value && form.id) {
      await budgetStore.update(form.id, payload);
    } else {
      await budgetStore.create(payload);
    }
    mainModalInstance?.hide();
    resetForm();
  } catch (e) {
    console.error("Failed to save budget", e);
  } finally {
    saving.value = false;
  }
}

/* Delete */
async function confirmDelete() {
  if (!deletingId.value) return;

  try {
    await budgetStore.remove(deletingId.value);
    deleteModalInstance?.hide();
    deletingId.value = null;
  } catch (e) {
    console.error("Failed to delete budget", e);
  }
}

/* Exposed methods */
async function openCreate() {
  isEdit.value = false;
  resetForm();
  mainModalInstance?.show();
  nextTick(() => titleInputEl.value?.focus?.());

  await Promise.all([
    projectStore ? loadProjects() : Promise.resolve(),
    categoryStore ? loadCategories() : Promise.resolve(),
    taxStore ? loadTaxOptions() : Promise.resolve(),
  ]);
}

async function openEdit(row) {
  if (!row?.id) return;

  isEdit.value = true;
  resetForm();

  // Ensure dropdown data exists BEFORE preselecting
  await Promise.all([
    projectStore ? loadProjects() : Promise.resolve(),
    categoryStore ? loadCategories() : Promise.resolve(),
    taxStore ? loadTaxOptions() : Promise.resolve(),
  ]);

  const full = row || (await budgetStore.fetchOne(row.id));

  form.id = full.id;
  form.title = full.title || "";
  form.budget_type = full.budget_type || "category";
  form.tax_amount = Number(full.tax_amount || 0);
  form.notes = full.notes || "";
  form.is_active = full.is_active ?? true;

  startDate.value = fromYmd(full.start_date);
  endDate.value = fromYmd(full.end_date);

  revenueItems.value =
    Array.isArray(full.revenue_items) && full.revenue_items.length
      ? full.revenue_items.map((r) => ({ title: r.title || "", amount: r.amount || 0 }))
      : [{ title: "", amount: 0 }];

  expenseItems.value =
    Array.isArray(full.expense_items) && full.expense_items.length
      ? full.expense_items.map((e) => ({ title: e.title || "", amount: e.amount || 0 }))
      : [{ title: "", amount: 0 }];

  // Preselect project
  selectedProject.value = null;
  if (full.project_id) {
    selectedProject.value = projectOptions.value.find((p) => p.id === full.project_id) || null;
  } else if (full.project?.id) {
    selectedProject.value = projectOptions.value.find((p) => p.id === full.project.id) || null;
  }

  // Preselect category
  selectedCategory.value = null;
  if (full.account_category_id) {
    selectedCategory.value =
      categoryOptions.value.find((c) => c.id === full.account_category_id) || null;
  } else if (full.account_category?.id) {
    selectedCategory.value =
      categoryOptions.value.find((c) => c.id === full.account_category.id) || null;
  }

  // Preselect tax
  selectedTax.value = null;
  if (full.tax_id) {
    const foundTax = taxOptions.value.find((t) => t.id === full.tax_id);
    if (foundTax) selectedTax.value = foundTax;
    else if (full.tax_rate != null) {
      selectedTax.value = {
        id: full.tax_id,
        name: "Saved tax",
        rate: Number(full.tax_rate),
        label: `Saved tax (${Number(full.tax_rate)}%)`,
      };
    }
  }

  computeTotals();
  mainModalInstance?.show();
  nextTick(() => titleInputEl.value?.focus?.());
}

function openDelete(row) {
  deletingId.value = row?.id || null;
  deleteModalInstance?.show();
}

async function loadProjects(q = "") {
  if (!projectStore) return;

  try {
    projectsLoading.value = true;
    let res;

    if (typeof projectStore.fetch === "function") {
      res = await projectStore.fetch({ q, per_page: 50 });
    } else if (typeof projectStore.list === "function") {
      res = await projectStore.list({ q, per_page: 50 });
    }

    const arr = (res && res.data && res.data.data) || projectStore.items || projectStore.projects || [];
    projectOptions.value = arr.map((p) => ({
      id: p.id,
      name: p.name,
      code: p.project_code || p.code || null,
      label: p.project_code ? `${p.project_code} - ${p.name}` : p.name,
    }));
  } catch (e) {
    console.error("Failed to load projects", e);
  } finally {
    projectsLoading.value = false;
  }
}

async function loadCategories(q = "") {
  if (!categoryStore) return;

  try {
    categoriesLoading.value = true;
    let res;

    if (typeof categoryStore.fetch === "function") {
      res = await categoryStore.fetch({ q, per_page: 50 });
    } else if (typeof categoryStore.list === "function") {
      res = await categoryStore.list({ q, per_page: 50 });
    }

    const arr = (res && res.data && res.data.data) || categoryStore.items || categoryStore.accountCategories || [];
    categoryOptions.value = arr.map((c) => ({
      id: c.id,
      name: c.name,
      sub: c.sub_category_name || "",
      label: c.sub_category_name ? `${c.name} - ${c.sub_category_name}` : c.name,
    }));
  } catch (e) {
    console.error("Failed to load categories", e);
  } finally {
    categoriesLoading.value = false;
  }
}

function chooseProject(option) {
  selectedProject.value = option;
}
function chooseCategory(option) {
  selectedCategory.value = option;
}

/* Switch type -> clear opposite */
watch(
  () => form.budget_type,
  (val) => {
    if (val === "project") selectedCategory.value = null;
    if (val === "category") selectedProject.value = null;
    attempted.value = false;
  }
);

/* Recompute totals when tax changes or amounts change */
watch(
  () => selectedTax.value,
  () => computeTotals()
);

/* Init modals properly */
function onMainHidden() {
  // keep resetForm() only when user closes (not while editing)
  // but it’s safe; your edit/open functions rehydrate anyway
  resetForm();
}
function onDeleteHidden() {
  deletingId.value = null;
}

defineExpose({ openCreate, openEdit, openDelete });

onMounted(() => {
  if (mainModalEl.value) {
    mainModalInstance = bootstrap.Modal.getOrCreateInstance(mainModalEl.value, {
      backdrop: "static",
      keyboard: true,
      focus: true,
    });
    mainModalEl.value.addEventListener("hidden.bs.modal", onMainHidden);
    mainModalEl.value.addEventListener("shown.bs.modal", () =>
      nextTick(() => titleInputEl.value?.focus?.())
    );
  }
  if (deleteModalEl.value) {
    deleteModalInstance = bootstrap.Modal.getOrCreateInstance(deleteModalEl.value, {
      backdrop: "static",
      keyboard: true,
      focus: true,
    });
    deleteModalEl.value.addEventListener("hidden.bs.modal", onDeleteHidden);
  }

  if (taxStore && !taxOptions.value.length) loadTaxOptions();
  if (projectStore && !projectOptions.value.length) loadProjects();
  if (categoryStore && !categoryOptions.value.length) loadCategories();
});

onBeforeUnmount(() => {
  mainModalEl.value?.removeEventListener("hidden.bs.modal", onMainHidden);
  deleteModalEl.value?.removeEventListener("hidden.bs.modal", onDeleteHidden);
});

/* Formatting helper */
function money(n) {
  const x = Number(n || 0);
  return x.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
</script>

<template>
  <!-- ADD / EDIT BUDGET -->
  <div class="modal fade dq-modal" id="budget_modal" tabindex="-1" ref="mainModalEl" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content dq-modal-card">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="d-flex align-items-center gap-3">
            <span class="dq-icon-badge" :class="isEdit ? 'dq-icon-badge-warn' : ''">
              <i :class="isEdit ? 'ti ti-edit' : 'ti ti-chart-pie'"></i>
            </span>

            <div class="lh-1">
              <h4 class="modal-title mb-1">
                {{ isEdit ? "Edit Budget" : "Create Budget" }}
              </h4>
              <div class="dq-subtitle">
                Plan revenues & expenses, apply tax, and instantly see profit and budget amount.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn dq-close-btn"
            data-bs-dismiss="modal"
            aria-label="Close"
            :disabled="saving"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submit" novalidate>
          <div class="modal-body dq-modal-body">
            <!-- KPI Summary -->
            <div class="dq-kpis">
              <div class="dq-kpi">
                <div class="dq-kpi-label">Overall Revenue (A)</div>
                <div class="dq-kpi-value">{{ money(form.total_revenue) }}</div>
              </div>
              <div class="dq-kpi">
                <div class="dq-kpi-label">Overall Expense (B)</div>
                <div class="dq-kpi-value">{{ money(form.total_expense) }}</div>
              </div>
              <div class="dq-kpi">
                <div class="dq-kpi-label">Expected Profit (C)</div>
                <div class="dq-kpi-value">{{ money(form.expected_profit) }}</div>
              </div>
              <div class="dq-kpi">
                <div class="dq-kpi-label">Tax (D)</div>
                <div class="dq-kpi-value">{{ money(form.tax_amount) }}</div>
              </div>
              <div class="dq-kpi dq-kpi-strong">
                <div class="dq-kpi-label">Budget Amount (E)</div>
                <div class="dq-kpi-value">{{ money(form.budget_amount) }}</div>
              </div>
            </div>

            <div class="dq-form-grid">
              <!-- Title -->
              <div class="dq-field">
                <label class="form-label dq-label">Budget Title <span class="text-danger">*</span></label>
                <div class="dq-input-wrap">
                  <span class="dq-input-icon"><i class="ti ti-id"></i></span>
                  <input
                    ref="titleInputEl"
                    type="text"
                    class="form-control dq-input"
                    v-model="form.title"
                    placeholder="Office Supplies, Salary 2024, etc."
                    autocomplete="off"
                    :aria-invalid="attempted && !!errors.title"
                  />
                </div>
                <div v-if="attempted && errors.title" class="dq-error">
                  <i class="ti ti-alert-circle me-1"></i>{{ errors.title }}
                </div>
                <div v-else class="dq-help">Use a clear name that will appear on reports and listings.</div>
              </div>

              <!-- Type segmented -->
              <div class="dq-field">
                <label class="form-label dq-label">Budget type</label>
                <div class="dq-segment">
                  <button
                    type="button"
                    class="dq-seg"
                    :class="{ active: form.budget_type === 'project' }"
                    @click="form.budget_type = 'project'"
                  >
                    <i class="ti ti-briefcase me-2"></i>Project
                  </button>
                  <button
                    type="button"
                    class="dq-seg"
                    :class="{ active: form.budget_type === 'category' }"
                    @click="form.budget_type = 'category'"
                  >
                    <i class="ti ti-tags me-2"></i>Category
                  </button>
                </div>
                <div v-if="attempted && errors.link" class="dq-error mt-2">
                  <i class="ti ti-alert-circle me-1"></i>{{ errors.link }}
                </div>
              </div>

              <!-- Project / Category selector -->
              <div class="dq-field" v-if="form.budget_type === 'project'">
                <label class="form-label dq-label">Project <span class="text-danger">*</span></label>

                <div class="dropdown w-100">
                  <button class="btn dq-dd-btn w-100" type="button" data-bs-toggle="dropdown">
                    <span class="text-truncate">
                      {{ selectedProject ? selectedProject.label : "Select project" }}
                    </span>
                    <i class="ti ti-chevron-down"></i>
                  </button>

                  <div class="dropdown-menu dq-dd-menu w-100">
                    <div class="dq-dd-search">
                      <i class="ti ti-search"></i>
                      <input
                        v-model="projectQuery"
                        @input="loadProjects(projectQuery)"
                        type="text"
                        class="form-control dq-dd-input"
                        placeholder="Search project..."
                      />
                    </div>

                    <div class="dq-dd-list">
                      <div v-if="projectsLoading" class="dq-dd-state">Loading projects…</div>
                      <button
                        v-for="p in projectOptions"
                        :key="p.id"
                        type="button"
                        class="dropdown-item dq-dd-item"
                        @click="chooseProject(p)"
                      >
                        <div class="dq-dd-title">{{ p.name }}</div>
                        <div class="dq-dd-sub" v-if="p.code">{{ p.code }}</div>
                      </button>
                      <div v-if="!projectsLoading && !projectOptions.length" class="dq-dd-state">
                        No project found
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-field" v-else>
                <label class="form-label dq-label">Category <span class="text-danger">*</span></label>

                <div class="dropdown w-100">
                  <button class="btn dq-dd-btn w-100" type="button" data-bs-toggle="dropdown">
                    <span class="text-truncate">
                      {{ selectedCategory ? selectedCategory.label : "Select category" }}
                    </span>
                    <i class="ti ti-chevron-down"></i>
                  </button>

                  <div class="dropdown-menu dq-dd-menu w-100">
                    <div class="dq-dd-search">
                      <i class="ti ti-search"></i>
                      <input
                        v-model="categoryQuery"
                        @input="loadCategories(categoryQuery)"
                        type="text"
                        class="form-control dq-dd-input"
                        placeholder="Search category..."
                      />
                    </div>

                    <div class="dq-dd-list">
                      <div v-if="categoriesLoading" class="dq-dd-state">Loading categories…</div>
                      <button
                        v-for="c in categoryOptions"
                        :key="c.id"
                        type="button"
                        class="dropdown-item dq-dd-item"
                        @click="chooseCategory(c)"
                      >
                        <div class="dq-dd-title">{{ c.name }}</div>
                        <div class="dq-dd-sub" v-if="c.sub">{{ c.sub }}</div>
                      </button>
                      <div v-if="!categoriesLoading && !categoryOptions.length" class="dq-dd-state">
                        No category found
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Dates -->
              <div class="dq-two">
                <div class="dq-field">
                  <label class="form-label dq-label">Start Date</label>
                  <div class="input-icon-end position-relative">
                    <date-picker
                      v-model="startDate"
                      class="form-control datetimepicker dq-input"
                      placeholder="dd/mm/yyyy"
                      :editable="true"
                      :clearable="false"
                      :input-format="dateFormat"
                    />
                    <span class="input-icon-addon"><i class="ti ti-calendar text-gray-7"></i></span>
                  </div>
                </div>

                <div class="dq-field">
                  <label class="form-label dq-label">End Date</label>
                  <div class="input-icon-end position-relative">
                    <date-picker
                      v-model="endDate"
                      class="form-control datetimepicker dq-input"
                      placeholder="dd/mm/yyyy"
                      :editable="true"
                      :clearable="false"
                      :input-format="dateFormat"
                    />
                    <span class="input-icon-addon"><i class="ti ti-calendar text-gray-7"></i></span>
                  </div>
                  <div v-if="attempted && errors.dates" class="dq-error mt-2">
                    <i class="ti ti-alert-circle me-1"></i>{{ errors.dates }}
                  </div>
                </div>
              </div>

              <!-- Revenue + Expense sections -->
              <div class="dq-split">
                <!-- Revenues -->
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div>
                      <div class="dq-card-title">Expected Revenues</div>
                      <div class="dq-card-sub">Add all expected income lines.</div>
                    </div>
                    <button type="button" class="btn btn-sm dq-mini-btn" @click.prevent="addRevenueRow">
                      <i class="ti ti-plus me-1"></i>Add
                    </button>
                  </div>

                  <div class="dq-lines">
                    <div class="dq-line" v-for="(rev, ri) in revenueItems" :key="'rev-' + ri">
                      <div class="dq-line-left">
                        <input
                          type="text"
                          class="form-control dq-input"
                          v-model="rev.title"
                          placeholder="Revenue title"
                        />
                      </div>
                      <div class="dq-line-right">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          class="form-control dq-input text-end"
                          v-model.number="rev.amount"
                          @input="computeTotals"
                          placeholder="0.00"
                        />
                      </div>
                      <button
                        v-if="revenueItems.length > 1"
                        type="button"
                        class="btn dq-icon-btn"
                        @click.prevent="removeRevenueRow(ri)"
                        title="Remove"
                      >
                        <i class="ti ti-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Expenses -->
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div>
                      <div class="dq-card-title">Expected Expenses</div>
                      <div class="dq-card-sub">Add all expected cost lines.</div>
                    </div>
                    <button type="button" class="btn btn-sm dq-mini-btn" @click.prevent="addExpenseRow">
                      <i class="ti ti-plus me-1"></i>Add
                    </button>
                  </div>

                  <div class="dq-lines">
                    <div class="dq-line" v-for="(exp, ei) in expenseItems" :key="'exp-' + ei">
                      <div class="dq-line-left">
                        <input
                          type="text"
                          class="form-control dq-input"
                          v-model="exp.title"
                          placeholder="Expense title"
                        />
                      </div>
                      <div class="dq-line-right">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          class="form-control dq-input text-end"
                          v-model.number="exp.amount"
                          @input="computeTotals"
                          placeholder="0.00"
                        />
                      </div>
                      <button
                        v-if="expenseItems.length > 1"
                        type="button"
                        class="btn dq-icon-btn"
                        @click.prevent="removeExpenseRow(ei)"
                        title="Remove"
                      >
                        <i class="ti ti-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tax selector + active + notes -->
              <div class="dq-two">
                <div class="dq-field">
                  <label class="form-label dq-label">Tax (optional)</label>
                  <div class="dropdown w-100">
                    <button class="btn dq-dd-btn w-100" type="button" data-bs-toggle="dropdown">
                      <span class="text-truncate">
                        {{ selectedTax ? `${selectedTax.name} (${selectedTax.rate}%)` : "Select tax" }}
                      </span>
                      <i class="ti ti-chevron-down"></i>
                    </button>

                    <div class="dropdown-menu dq-dd-menu w-100">
                      <div class="dq-dd-search">
                        <i class="ti ti-search"></i>
                        <input
                          v-model="taxQuery"
                          @input="loadTaxOptions(taxQuery)"
                          type="text"
                          class="form-control dq-dd-input"
                          placeholder="Search tax..."
                        />
                      </div>

                      <div class="dq-dd-list">
                        <div v-if="taxesLoading" class="dq-dd-state">Loading taxes…</div>
                        <button
                          v-for="t in taxOptions"
                          :key="t.id"
                          type="button"
                          class="dropdown-item dq-dd-item"
                          @click="chooseTax(t)"
                        >
                          <div class="dq-dd-title">{{ t.name }}</div>
                          <div class="dq-dd-sub">{{ t.rate }}%</div>
                        </button>
                        <div v-if="!taxesLoading && !taxOptions.length" class="dq-dd-state">
                          No tax found
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="dq-help mt-2">Tax is calculated from profit (C) using the selected rate.</div>
                </div>

                <div class="dq-field">
                  <label class="form-label dq-label">Active</label>
                  <div class="dq-switch-row">
                    <div class="dq-switch-text">
                      <div class="dq-switch-title">Status</div>
                      <div class="dq-switch-sub">Inactive budgets are hidden from new selections.</div>
                    </div>
                    <label class="dq-switch">
                      <input type="checkbox" v-model="form.is_active" />
                      <span class="dq-slider"></span>
                    </label>
                  </div>
                </div>
              </div>

              <div class="dq-field">
                <label class="form-label dq-label">Notes</label>
                <textarea rows="3" class="form-control dq-textarea" v-model="form.notes" placeholder="Optional…"></textarea>
              </div>

              <!-- Tip -->
              <div class="dq-tip">
                <div class="dq-tip-icon"><i class="ti ti-bulb"></i></div>
                <div>
                  <div class="dq-tip-title">Quick tip</div>
                  <div class="dq-tip-text">Use consistent line items to compare budgets month-to-month.</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sticky footer -->
          <div class="modal-footer dq-modal-footer">
            <button type="button" class="btn btn-light dq-btn-soft" data-bs-dismiss="modal" :disabled="saving">
              Cancel
            </button>
            <button type="submit" class="btn btn-primary dq-btn-primary" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              <span v-if="saving">{{ isEdit ? "Saving…" : "Creating…" }}</span>
              <span v-else>{{ isEdit ? "Save Changes" : "Create Budget" }}</span>
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- DELETE MODAL -->
  <div class="modal fade dq-modal" id="delete_modal_budget" tabindex="-1" ref="deleteModalEl" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content dq-modal-card">
        <div class="modal-body dq-delete-body">
          <div class="dq-danger-hero">
            <span class="dq-danger-badge"><i class="ti ti-trash-x"></i></span>
          </div>
          <h4 class="mb-1 dq-delete-title">Confirm delete</h4>
          <p class="mb-3 dq-delete-subtitle">This action permanently removes the budget. You can’t undo this.</p>
          <div class="dq-delete-warning">
            <i class="ti ti-alert-triangle me-2"></i>
            Tip: Consider setting it to <strong>Inactive</strong> if it’s used in reports.
          </div>

          <div class="d-flex justify-content-center gap-2 mt-4">
            <button type="button" class="btn btn-light dq-btn-soft" data-bs-dismiss="modal">
              Cancel
            </button>
            <button type="button" class="btn btn-danger dq-btn-danger" @click="confirmDelete">
              Yes, Delete <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ===== Same "dq" premium system as your other modals ===== */
.dq-modal-card {
  border: 0;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 22px 60px rgba(16, 24, 40, 0.18);
}
.dq-modal-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 18px 18px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 1), rgba(255, 255, 255, 1));
}
.dq-subtitle {
  font-size: 0.85rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 2px;
}
.dq-close-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.dq-close-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(16, 24, 40, 0.08);
}

.dq-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.12);
  color: rgba(37, 99, 235, 1);
  font-size: 20px;
}
.dq-icon-badge-warn {
  background: rgba(245, 158, 11, 0.14);
  color: rgba(217, 119, 6, 1);
}

.dq-modal-body {
  padding: 18px;
  max-height: 76vh;
  overflow: auto;
}

.dq-form-grid {
  display: grid;
  gap: 14px;
}

.dq-label {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.9);
  margin-bottom: 6px;
}

.dq-input-wrap {
  position: relative;
}
.dq-input-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(17, 24, 39, 0.45);
  z-index: 3;
  pointer-events: none;
}
.dq-input {
  padding-left: 40px !important;
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  min-height: 44px;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
.dq-input:focus {
  border-color: rgba(59, 130, 246, 0.45) !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.12) !important;
}
.dq-textarea {
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.dq-textarea:focus {
  border-color: rgba(59, 130, 246, 0.45) !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.10) !important;
}
.dq-help {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 6px;
}
.dq-error {
  margin-top: 6px;
  font-size: 0.82rem;
  font-weight: 800;
  color: rgba(220, 38, 38, 1);
  display: inline-flex;
  align-items: center;
}

.dq-two {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
@media (max-width: 991px) {
  .dq-two { grid-template-columns: 1fr; }
}

/* KPIs */
.dq-kpis {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  margin-bottom: 14px;
}
@media (max-width: 991px) {
  .dq-kpis { grid-template-columns: repeat(2, 1fr); }
}
.dq-kpi {
  border: 1px solid rgba(0,0,0,0.06);
  background: rgba(248, 250, 252, 0.7);
  border-radius: 16px;
  padding: 10px 12px;
}
.dq-kpi-strong {
  background: rgba(59, 130, 246, 0.10);
  border-color: rgba(59, 130, 246, 0.16);
}
.dq-kpi-label {
  font-size: 0.78rem;
  color: rgba(17, 24, 39, 0.6);
  font-weight: 700;
}
.dq-kpi-value {
  margin-top: 2px;
  font-weight: 950;
  letter-spacing: -0.02em;
  color: rgba(17, 24, 39, 0.92);
}

/* Segmented */
.dq-segment {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.dq-seg {
  border-radius: 14px;
  padding: 10px 12px;
  border: 1px solid rgba(0,0,0,0.08);
  background: #fff;
  font-weight: 900;
  color: rgba(17,24,39,0.75);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.12s ease;
}
.dq-seg:hover { transform: translateY(-1px); }
.dq-seg.active {
  background: rgba(59, 130, 246, 0.10);
  border-color: rgba(59, 130, 246, 0.20);
  color: rgba(37, 99, 235, 1);
}

/* Dropdown (searchable) */
.dq-dd-btn {
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(0,0,0,0.08);
  background: #fff;
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
}
.dq-dd-menu {
  border-radius: 16px;
  border: 1px solid rgba(0,0,0,0.06);
  box-shadow: 0 18px 40px rgba(16, 24, 40, 0.16);
  padding: 10px;
}
.dq-dd-search {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 14px;
  border: 1px solid rgba(0,0,0,0.06);
  background: rgba(248, 250, 252, 0.8);
  margin-bottom: 8px;
}
.dq-dd-input {
  border: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
  padding: 0 !important;
}
.dq-dd-list {
  max-height: 240px;
  overflow: auto;
  padding-right: 2px;
}
.dq-dd-item {
  border-radius: 14px;
  padding: 10px 10px;
}
.dq-dd-item:hover {
  background: rgba(59,130,246,0.08);
}
.dq-dd-title {
  font-weight: 900;
  color: rgba(17,24,39,0.9);
}
.dq-dd-sub {
  font-size: 0.78rem;
  color: rgba(17,24,39,0.55);
}
.dq-dd-state {
  text-align: center;
  padding: 14px 10px;
  font-size: 0.85rem;
  color: rgba(17, 24, 39, 0.55);
}

/* Split cards */
.dq-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
@media (max-width: 991px) {
  .dq-split { grid-template-columns: 1fr; }
}
.dq-card {
  border-radius: 18px;
  border: 1px solid rgba(0,0,0,0.06);
  background: rgba(255,255,255,0.96);
  padding: 12px;
}
.dq-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}
.dq-card-title {
  font-weight: 950;
  color: rgba(17,24,39,0.92);
}
.dq-card-sub {
  font-size: 0.82rem;
  color: rgba(17,24,39,0.55);
  margin-top: 2px;
}
.dq-mini-btn {
  border-radius: 12px;
  border: 1px solid rgba(59,130,246,0.22);
  background: rgba(59,130,246,0.10);
  color: rgba(37,99,235,1);
  font-weight: 900;
}
.dq-lines { display: grid; gap: 10px; }
.dq-line {
  display: grid;
  grid-template-columns: 1fr 150px 40px;
  gap: 10px;
  align-items: center;
}
@media (max-width: 520px) {
  .dq-line { grid-template-columns: 1fr; }
}
.dq-icon-btn {
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,0.06);
  background: rgba(248,250,252,0.8);
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.dq-icon-btn:hover { background: rgba(239,68,68,0.08); border-color: rgba(239,68,68,0.14); color: rgba(220,38,38,1); }

/* Switch (same as categories modal) */
.dq-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border-radius: 16px;
  border: 1px solid rgba(0,0,0,0.06);
  background: rgba(248, 250, 252, 0.8);
}
.dq-switch-title { font-weight: 950; color: rgba(17,24,39,0.9); }
.dq-switch-sub { font-size: 0.82rem; color: rgba(17,24,39,0.6); margin-top: 2px; }

.dq-switch { position: relative; width: 46px; height: 26px; display: inline-block; flex: 0 0 auto; }
.dq-switch input { display: none; }
.dq-slider { position: absolute; cursor: pointer; inset: 0; background: rgba(148,163,184,0.55); border-radius: 999px; transition: 0.18s ease; }
.dq-slider:before {
  content: "";
  position: absolute;
  height: 20px; width: 20px;
  left: 3px; top: 3px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 8px 18px rgba(16, 24, 40, 0.18);
  transition: 0.18s ease;
}
.dq-switch input:checked + .dq-slider { background: rgba(37,99,235,0.75); }
.dq-switch input:checked + .dq-slider:before { transform: translateX(20px); }

/* Tip */
.dq-tip {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 16px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.12);
}
.dq-tip-icon {
  width: 36px; height: 36px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(99, 102, 241, 0.14);
  color: rgba(79, 70, 229, 1);
}
.dq-tip-title { font-weight: 950; color: rgba(17,24,39,0.9); margin-bottom: 2px; }
.dq-tip-text { font-size: 0.83rem; color: rgba(17,24,39,0.6); }

/* Sticky footer */
.dq-modal-footer {
  position: sticky;
  bottom: 0;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(0,0,0,0.06);
  padding: 14px 18px;
}
.dq-btn-soft { border-radius: 12px; padding: 10px 14px; }
.dq-btn-primary { border-radius: 12px; padding: 10px 14px; box-shadow: 0 14px 26px rgba(37, 99, 235, 0.18); }

/* Delete */
.dq-delete-body { padding: 18px; text-align: center; }
.dq-danger-hero { display: flex; justify-content: center; margin-top: 6px; }
.dq-danger-badge {
  width: 64px; height: 64px;
  border-radius: 20px;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(239, 68, 68, 0.12);
  color: rgba(220, 38, 38, 1);
  font-size: 26px;
}
.dq-delete-title { font-weight: 950; letter-spacing: -0.02em; }
.dq-delete-subtitle { color: rgba(17, 24, 39, 0.65); font-size: 0.92rem; line-height: 1.35rem; }
.dq-delete-warning {
  text-align: left;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(245, 158, 11, 0.10);
  border: 1px solid rgba(245, 158, 11, 0.14);
  color: rgba(120, 53, 15, 0.95);
  font-size: 0.84rem;
}
.dq-btn-danger { border-radius: 12px; padding: 10px 14px; box-shadow: 0 14px 26px rgba(220, 38, 38, 0.14); }
</style>
